/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { OuiButtonEmpty } from '../../button';
export var OuiHeaderLink = ({
  isActive,
  className,
  ...rest
}) => {
  const classes = classNames('ouiHeaderLink', {
    'ouiHeaderLink-isActive': isActive
  }, className);
  const props = {
    color: isActive ? 'primary' : 'text',
    ...rest,
    className: classes
  };
  return <OuiButtonEmpty {...props} />;
};
OuiHeaderLink.propTypes = {
  href: PropTypes.string,
  onClick: PropTypes.func,
  /**
     * Any of our named colors
     */
  /**
     * Any of our named colors
     */
  color: PropTypes.oneOf(["primary", "danger", "text", "ghost", "success", "warning"]),
  size: PropTypes.oneOf(["xs", "s", "l"]),
  /**
     * Ensure the text of the button sits flush to the left, right, or both sides of its container
     */
  /**
     * Ensure the text of the button sits flush to the left, right, or both sides of its container
     */
  flush: PropTypes.oneOf(["left", "right", "both"]),
  /**
     * `disabled` is also allowed
     */
  /**
     * `disabled` is also allowed
     */
  isDisabled: PropTypes.bool,
  /**
     * Force disables the button and changes the icon to a loading spinner
     */
  /**
     * Force disables the button and changes the icon to a loading spinner
     */
  isLoading: PropTypes.bool,
  /**
     * Applies the boolean state as the `aria-pressed` property to create a toggle button.
     * *Only use when the readable text does not change between states.*
     */
  /**
     * Applies the boolean state as the `aria-pressed` property to create a toggle button.
     * *Only use when the readable text does not change between states.*
     */
  isSelected: PropTypes.bool,
  target: PropTypes.string,
  rel: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit"]),
  buttonRef: PropTypes.any,
  /**
     * Object of props passed to the <span/> wrapping the button's content
     */
  contentProps: PropTypes.any,
  /**
     * Any `type` accepted by OuiIcon
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addBookmark", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "analyzeEvent", "annotation", "anomalyDetection", "apmApp", "apmTrace", "apps", "appSearchApp", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "bookOpen", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "chatLeft", "chatRight", "check", "checkInCircleEmpty", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "color", "compass", "compute", "console", "consoleApp", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "crossInCircleEmpty", "crossInCircleFilled", "currency", "cut", "dashboard", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentation", "documentEdit", "documents", "dot", "download", "dockedBottom", "dockedDetached", "dockedLeft", "dockedRight", "dockedTakeover", "dockedTop", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "functionAdd", "gear", "generate", "generateContent", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "history", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "inputOutput", "inspect", "integrationGeneral", "integrationObservability", "integrationSearch", "integrationSecurity", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "layers", "lensApp", "lineChart", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOpenSearch", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "polygon", "popout", "power", "pulse", "push", "questionInCircle", "quote", "radar", "radius", "recent", "recentlyViewedApp", "redeploy", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "rocket", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sparkleFilled", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "thumbsDown", "thumbsUp", "timeline", "timelionApp", "timeslider", "training", "trash", "upgradeAssistantApp", "uptimeApp", "undeploy", "unfold", "unlink", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visBuilder", "visBuilderSavedObject", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visQueryDQL", "visQueryPPL", "visQueryPromQL", "visQuerySQL", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram", "wsAnalytics", "wsEssentials", "wsObservability", "wsSearch", "wsSecurityAnalytics", "wsSelector", "navAdministration", "navAiFlow", "navAlerting", "navAnomalyDetection", "navDashboards", "navData", "navDetectionRules", "navDevtools", "navDiscover", "navExperiments", "navGetStarted", "navInfo", "navInfra", "navIntegrations", "navJudgements", "navManage", "navMaps", "navModels", "navNotebooks", "navNotifications", "navOverview", "navQuerySets", "navReports", "navSecurityCases", "navSecurityFindings", "navServiceMap", "navServices", "navSlos", "navThreatIntel", "navTicketing", "navUi", "navSearchConfigurationsln"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
  /**
     * Can only be one side `left` or `right`
     */
  iconSide: PropTypes.oneOf(["left", "right"]),
  /**
     * Object of props passed to the <span/> wrapping the content's text/children only (not icon)
     */
  textProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    ref: PropTypes.any,
    "data-text": PropTypes.string
  }),
  iconSize: PropTypes.oneOf(["s", "m"]),
  /**
     * The gap between the icon and the content
     */
  iconGap: PropTypes.oneOf(["s", "m", "none"]),
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /**
     * Simple prop to update color based on active state.
     * Can be overridden with `color`
     */
  isActive: PropTypes.bool
};
try {
  OuiHeaderLink.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src3672379046/src/src/components/header/header_links/header_link.tsx',
    description: '',
    displayName: 'OuiHeaderLink',
    methods: [],
    props: {
      color: {
        defaultValue: null,
        description: 'Any of our named colors',
        name: 'color',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"text" | "success" | "danger" | "warning" | "ghost" | "primary"',
          value: [{
            value: '"text"'
          }, {
            value: '"success"'
          }, {
            value: '"danger"'
          }, {
            value: '"warning"'
          }, {
            value: '"ghost"'
          }, {
            value: '"primary"'
          }]
        }
      },
      size: {
        defaultValue: null,
        description: '',
        name: 'size',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "l"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"l"'
          }]
        }
      },
      flush: {
        defaultValue: null,
        description: 'Ensure the text of the button sits flush to the left, right, or both sides of its container',
        name: 'flush',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"left" | "right" | "both"',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"both"'
          }]
        }
      },
      isDisabled: {
        defaultValue: null,
        description: '`disabled` is also allowed',
        name: 'isDisabled',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Force disables the button and changes the icon to a loading spinner',
        name: 'isLoading',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isSelected: {
        defaultValue: null,
        description: 'Applies the boolean state as the `aria-pressed` property to create a toggle button.\n' + '*Only use when the readable text does not change between states.*',
        name: 'isSelected',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      target: {
        defaultValue: null,
        description: '',
        name: 'target',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string | (string & {})'
        }
      },
      rel: {
        defaultValue: null,
        description: '',
        name: 'rel',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      type: {
        defaultValue: null,
        description: '',
        name: 'type',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'ButtonHTMLAttributes'
        }, {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AnchorHTMLAttributes'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "submit"',
          value: [{
            value: '"button"'
          }, {
            value: '"submit"'
          }]
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '',
        name: 'buttonRef',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLButtonElement | HTMLAnchorElement>'
        }
      },
      contentProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the button's content",
        name: 'contentProps',
        parent: {
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_empty/button_empty.tsx',
          name: 'CommonOuiButtonEmptyProps'
        }],
        required: false,
        type: {
          name: 'OuiButtonContentType'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Any `type` accepted by OuiIcon',
        name: 'iconType',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconSide: {
        defaultValue: null,
        description: 'Can only be one side `left` or `right`',
        name: 'iconSide',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonContentIconSide',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }]
        }
      },
      textProps: {
        defaultValue: null,
        description: "Object of props passed to the <span/> wrapping the content's text/children only (not icon)",
        name: 'textProps',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: "HTMLAttributes<HTMLSpanElement> & CommonProps & { ref?: Ref<HTMLSpanElement>; 'data-text'?: string; }"
        }
      },
      iconSize: {
        defaultValue: null,
        description: '',
        name: 'iconSize',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      },
      iconGap: {
        defaultValue: null,
        description: 'The gap between the icon and the content',
        name: 'iconGap',
        parent: {
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        },
        declarations: [{
          fileName: 'src/src/components/button/button_content.tsx',
          name: 'OuiButtonContentProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'ButtonContentIconGap',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"none"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'src/src/components/common.ts',
          name: 'TypeLiteral'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'DOMAttributes'
        }],
        required: false,
        type: {
          name: 'MouseEventHandler<HTMLAnchorElement> | MouseEventHandler<HTMLButtonElement>'
        }
      },
      isActive: {
        defaultValue: null,
        description: 'Simple prop to update color based on active state.\n' + 'Can be overridden with `color`',
        name: 'isActive',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/header/header_links/header_link.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['AnchorHTMLAttributes', 'CommonOuiButtonEmptyProps', 'OuiButtonContentProps', 'CommonProps', 'ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}